/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react'
import { css, keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { DivisionJob, TimeoutReason } from '../hooks/useSchedulerRequest'
import DivisionProgressCards from './DivisionProgressCards'

// ─── Keyframe animations ─────────────────────────────────────
const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(56, 148, 255, 0.15); }
  50% { box-shadow: 0 0 20px rgba(56, 148, 255, 0.35); }
`

const progressPulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`

const textFadeSwap = keyframes`
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
`

// ─── Helpers ──────────────────────────────────────────────────
function getCuratingPhaseText(elapsedSeconds: number): string {
  if (elapsedSeconds < 20) {
    return 'Curating schedule...'
  }
  if (elapsedSeconds < 40) {
    return 'Optimizing the schedule...'
  }
  return 'Hang tight! Almost there...'
}

function sortJobsByPriority(
  jobs: DivisionJob[],
  dispatchOrder: string[] | undefined
): DivisionJob[] {
  if (!dispatchOrder || dispatchOrder.length === 0) {
    return jobs
  }
  const orderMap = new Map(dispatchOrder.map((id, idx) => [id, idx]))
  return [...jobs].sort((a, b) => {
    const ai = orderMap.get(a.division_id) ?? Number.MAX_SAFE_INTEGER
    const bi = orderMap.get(b.division_id) ?? Number.MAX_SAFE_INTEGER
    return ai - bi
  })
}

// ─── Props ────────────────────────────────────────────────────
export interface CuratingStateProps {
  isGenerating: boolean
  generatingDivisionIds: string[]
  divisions: Array<{ id: string; name: string }>
  schedulerRequest: {
    division_jobs?: DivisionJob[]
    division_dispatch_order?: string[]
    can_cancel?: boolean
  } | null
  divisionPriorityOrder: string[]
  isTimedOut: boolean
  timeoutReason: TimeoutReason | null
  keepWaiting: () => void
  onCancelClick: () => void
  generationStartTime: number | null
}

// ─── Component ────────────────────────────────────────────────
const CuratingState: React.FC<CuratingStateProps> = ({
  isGenerating,
  generatingDivisionIds,
  divisions,
  schedulerRequest,
  divisionPriorityOrder,
  isTimedOut,
  timeoutReason,
  keepWaiting,
  onCancelClick,
  generationStartTime,
}) => {
  const [curatingElapsed, setCuratingElapsed] = useState(() =>
    generationStartTime
      ? Math.floor((Date.now() - generationStartTime) / 1000)
      : 0
  )

  useEffect(() => {
    if (!isGenerating || !generationStartTime) {
      return
    }
    // Sync immediately on mount (covers tab-switch case)
    setCuratingElapsed(Math.floor((Date.now() - generationStartTime) / 1000))
    const id = setInterval(() => {
      setCuratingElapsed(Math.floor((Date.now() - generationStartTime) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [isGenerating, generationStartTime])

  const selectedDivNames = generatingDivisionIds.map(
    (id) => divisions.find((d) => d.id === id)?.name ?? id
  )

  const phaseText = getCuratingPhaseText(curatingElapsed)
  const divisionJobs = sortJobsByPriority(
    schedulerRequest?.division_jobs ?? [],
    divisionPriorityOrder.length > 0
      ? divisionPriorityOrder
      : schedulerRequest?.division_dispatch_order
  )
  const hasDivisionJobs = divisionJobs.length > 0

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 48px 0 32px;
        animation: ${fadeInUp} 0.4s ease-out;
      `}
    >
      {/* Central spinner + phase text */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 40px;
        `}
      >
        {/* Outer ring spinner */}
        <div
          css={css`
            position: relative;
            width: 72px;
            height: 72px;
            margin-bottom: 24px;
          `}
        >
          {/* Outer glow ring */}
          <div
            css={css`
              position: absolute;
              inset: 0;
              border-radius: 50%;
              border: 3px solid rgba(56, 148, 255, 0.12);
              animation: ${pulseGlow} 2s ease-in-out infinite;
            `}
          />
          {/* Spinning arc */}
          <div
            css={css`
              position: absolute;
              inset: 0;
              border-radius: 50%;
              border: 3px solid transparent;
              border-top-color: ${colors.INFO_300};
              border-right-color: ${colors.INFO_300};
              animation: ${spinAnimation} 1s linear infinite;
            `}
          />
          {/* Inner icon */}
          <div
            css={css`
              position: absolute;
              inset: 12px;
              border-radius: 50%;
              background: rgba(56, 148, 255, 0.08);
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
              <path
                d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z'
                fill={colors.INFO_300}
                opacity='0.7'
              />
            </svg>
          </div>
        </div>

        {/* Phase text with fade animation on change */}
        <p
          key={phaseText}
          css={css`
            font-family: 'Barlow Condensed', sans-serif;
            font-weight: 600;
            font-size: 22px;
            color: ${colors.WHITE};
            margin: 0 0 6px;
            animation: ${textFadeSwap} 0.35s ease-out;
          `}
        >
          {phaseText}
        </p>
        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 400;
            font-size: 13px;
            color: ${colors.NEUTRAL_50};
            margin: 0;
            animation: ${progressPulse} 2s ease-in-out infinite;
          `}
        >
          Building the best schedule for{' '}
          {hasDivisionJobs ? divisionJobs.length : selectedDivNames.length}{' '}
          {(hasDivisionJobs ? divisionJobs.length : selectedDivNames.length) ===
          1
            ? 'division'
            : 'divisions'}
        </p>
        <p
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 500;
            font-size: 13px;
            color: ${colors.NEUTRAL_70};
            margin: 8px 0 0;
          `}
        >
          {Math.floor(curatingElapsed / 60)}:
          {String(curatingElapsed % 60).padStart(2, '0')} elapsed
        </p>
      </div>

      {/* Per-division progress cards (from API) or fallback to simple list */}
      {hasDivisionJobs ? (
        <DivisionProgressCards divisionJobs={divisionJobs} />
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            width: 100%;
            max-width: 640px;
          `}
        >
          {selectedDivNames.map((name, index) => (
            <div
              key={index}
              css={css`
                display: flex;
                align-items: center;
                gap: 14px;
                padding: 14px 20px;
                background: linear-gradient(
                  135deg,
                  rgba(56, 148, 255, 0.06) 0%,
                  rgba(56, 148, 255, 0.02) 100%
                );
                border: 1px solid rgba(56, 148, 255, 0.15);
                border-radius: 10px;
                animation: ${fadeInUp} 0.4s ease-out ${index * 0.08}s both;
              `}
            >
              {/* Mini progress bar */}
              <div
                css={css`
                  width: 32px;
                  height: 4px;
                  border-radius: 2px;
                  overflow: hidden;
                  background: rgba(56, 148, 255, 0.15);
                  flex-shrink: 0;
                `}
              >
                <div
                  css={css`
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(
                      90deg,
                      transparent,
                      ${colors.INFO_300},
                      transparent
                    );
                    background-size: 200% 100%;
                    animation: ${shimmer} 1.5s linear infinite;
                  `}
                />
              </div>
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 500;
                  font-size: 14px;
                  color: ${colors.WHITE};
                `}
              >
                {name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Cancel button during generation */}
      {schedulerRequest?.can_cancel && (
        <button
          type='button'
          onClick={onCancelClick}
          css={css`
            margin-top: 24px;
            padding: 8px 20px;
            background: transparent;
            border: 1px solid ${colors.NEUTRAL_100};
            border-radius: 8px;
            color: ${colors.NEUTRAL_50};
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            &:hover {
              background: rgba(255, 255, 255, 0.05);
              border-color: ${colors.NEUTRAL_50};
              color: ${colors.WHITE};
            }
          `}
        >
          Cancel Generation
        </button>
      )}

      {/* Timeout / Stuck warning */}
      {isTimedOut && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 24px;
            margin-top: 24px;
            width: 100%;
            max-width: 640px;
            background-color: rgba(255, 193, 7, 0.08);
            border: 1px solid rgba(255, 193, 7, 0.3);
            border-radius: 10px;
            animation: ${fadeInUp} 0.3s ease-out;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 500;
              font-size: 14px;
              color: ${colors.WARNING_200};
              flex: 1;
            `}
          >
            {timeoutReason === 'stuck'
              ? 'No progress for 4 minutes. The scheduler may be stuck.'
              : 'Total time exceeded. The schedule may be too complex.'}
          </span>
          <div
            css={css`
              display: flex;
              gap: 8px;
              flex-shrink: 0;
              margin-left: 16px;
            `}
          >
            <button
              type='button'
              onClick={onCancelClick}
              css={css`
                padding: 6px 16px;
                background: transparent;
                border: 1px solid ${colors.WARNING_300};
                border-radius: 8px;
                color: ${colors.WARNING_200};
                font-family: 'Inter', sans-serif;
                font-size: 13px;
                cursor: pointer;
                transition: background 0.15s;
                &:hover {
                  background: rgba(255, 193, 7, 0.08);
                }
              `}
            >
              Cancel
            </button>
            {/* Only show "Keep Waiting" for stuck timeout, not total */}
            {timeoutReason === 'stuck' && (
              <button
                type='button'
                onClick={keepWaiting}
                css={css`
                  padding: 6px 16px;
                  background: ${colors.WARNING_300};
                  border: none;
                  border-radius: 8px;
                  color: ${colors.SECONDARY_300};
                  font-family: 'Inter', sans-serif;
                  font-size: 13px;
                  font-weight: 600;
                  cursor: pointer;
                  transition: opacity 0.15s;
                  &:hover {
                    opacity: 0.9;
                  }
                `}
              >
                Keep Waiting
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CuratingState
